import React, { useEffect } from 'react';

import { Flex, Text, useToast } from '@chakra-ui/react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';

import { deleteActivity, mutateActivity } from 'api/activities';
import { Divider, Radio } from 'components';
import { ActivityTypes } from 'enums/types';
import useRouteStore from 'stores/useRoute';

import CollectForm from './Collect';
import DeliveryForm from './Delivery';
import AirDispach from './Dispatch';
import Footer from './Footer';
import Partner from './Partner';
import Transhipment from './Transhipment';
import { handleDefaultValues } from './utils';

function ActivityForm({ type, form, index }) {
  if (type === ActivityTypes.Collect) {
    return (
      <>
        <Partner {...form} />
        <Divider mb={7} />
        <CollectForm
          index={index}
          {...form}
        />
      </>
    );
  }

  if (type === ActivityTypes.Delivery) {
    return (
      <>
        <Partner {...form} />
        <Divider mb={7} />
        <DeliveryForm {...form} />
      </>
    );
  }

  if (type === ActivityTypes.Transhipment) {
    return (
      <Transhipment
        index={index}
        {...form}
      />
    );
  }

  if (type === ActivityTypes.Dispatch) {
    return (
      <AirDispach
        index={index}
        {...form}
      />
    );
  }

  return null;
}

interface SectionFormProps {
  onSuccess?: () => void
  onCancel: () => void
  onClose: () => void
  data?: any
  index?: number
  isNew?: boolean
}
export default function SectionForm({
  onCancel, data, index, onClose, isNew, onSuccess = () => {},
}: SectionFormProps) {
  const { route, query, lastActivity } = useRouteStore();
  const toast = useToast();

  const form = useForm();
  const {
    control,
    watch,
    setValue,
    setError,
    handleSubmit,
    reset,
  } = form;
  useEffect(() => {
    if (isNew) {
      reset();
      handleDefaultValues(
        { setValue, defaultValues: { ...lastActivity, type: ActivityTypes.Collect } },
      );
    }
    if (data) {
      handleDefaultValues({ setValue, defaultValues: data });
    }
  }, [data, lastActivity, isNew]);

  const { mutate, isLoading } = useMutation(
    (formData: any) => mutateActivity({ data: formData, id: data?.id }),
    {
      onSuccess: () => {
        query.refetch();
        reset();
        onCancel();
        onSuccess();
        reset();
        onClose();
      },
      onError: ({ errors }) => {
        if (Array.isArray(errors)) {
          errors.forEach((error) => {
            setError(error.field, {
              type: error.rule,
              message: error.message,
            });
          });
        } else {
          onClose();
          reset();
          toast({
            title: 'Ocorreu um erro.',
            description: errors.message,
            status: 'error',
            duration: 5000,
            position: 'top-right',
            isClosable: true,
          });
        }
      },
    });

  const { mutate: onDelete, isLoading: isDeleting } = useMutation(
    () => deleteActivity(data.id),
    {
      onSuccess: () => {
        query.refetch();
        onCancel();
      },
      onError: (error: any) => {
        toast({
          title: 'Ocorreu um erro.',
          description: error.message,
          status: 'error',
          duration: 5000,
          position: 'top-right',
          isClosable: true,
        });
      },
    });
  const onSubmit = (formData) => {
    const params = {
      ...formData,
      route: {
        id: route.id,
      },
      destinationDrivers: formData.followupDrivers,
      destinationVehicle: formData.followupVehicle,
      modality: formData?.modality?.value,
    };
    if (formData.type === ActivityTypes.Transhipment) {
      params.address = {
        ...params.address,
        country: 'Brasil',
        city: params.address.city.name,
        state: params.address.state.name || params.address.state.code,
      };
    }
    mutate(params);
  };
  return (
    <Flex flexDir="column">
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex
            mb={7}
            flexDir="column">
            <Text mb={2}>Tipo:</Text>
            <Controller
              control={control}
              name="type"
              defaultValue={ActivityTypes.Collect}
              render={({ field }) => (
                <Radio
                  mb={7}
                  {...field}
                  options={index ? [
                    { label: 'Coleta', value: ActivityTypes.Collect },
                    { label: 'Entrega', value: ActivityTypes.Delivery },
                    { label: 'Transbordo', value: ActivityTypes.Transhipment },
                    { label: 'Despacho', value: ActivityTypes.Dispatch },
                  ] : [
                    { label: 'Coleta', value: ActivityTypes.Collect },
                  ]}

                />
              )}
            />
            <Divider />
          </Flex>
          <ActivityForm
            index={index}
            form={form}
            type={watch('type') ?? ActivityTypes.Collect}
          />
          <Divider />
          <Footer
            isLoading={isLoading || isDeleting}
            onDelete={onDelete}
            onCancel={() => {
              onCancel();
              onClose();
            }}
            isNew={!data}
          />
        </form>
      </FormProvider>
    </Flex>
  );
}
